import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Trash2, RefreshCw, Check, X, Pencil, FileText, Loader2, Sparkles,
  AlertTriangle, ChevronDown, ChevronUp, Plus, Ban, Wand2,
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";

const CURRENT_BANK_PROMPT_VERSION = 4;

// Hardcoded role-keyword → tools map, keyed by seniority tier
const ROLE_TOOLS = [
  {
    keywords: ["qa", "quality assurance", "quality engineer", "test engineer", "testing"],
    tools: {
      junior: ["Selenium", "Postman", "JIRA", "TestNG", "Manual Testing"],
      mid:    ["Selenium", "Playwright", "Postman", "TestNG", "REST Assured", "JIRA"],
      senior: ["Playwright", "Cypress", "Selenium Grid", "k6", "JMeter", "REST Assured", "CI/CD"],
      lead:   ["Playwright", "Cypress", "Selenium Grid", "k6", "JMeter", "Allure", "Docker", "CI/CD"],
    },
  },
  {
    keywords: ["frontend", "front-end", "front end", "ui developer", "react developer", "vue developer", "angular developer"],
    tools: {
      junior: ["React", "HTML", "CSS", "JavaScript", "Git"],
      mid:    ["React", "TypeScript", "Redux", "Next.js", "Tailwind CSS", "Jest"],
      senior: ["React", "TypeScript", "Next.js", "GraphQL", "Webpack", "Testing Library", "CI/CD"],
      lead:   ["React", "TypeScript", "Next.js", "GraphQL", "Micro Frontends", "Design Systems", "CI/CD"],
    },
  },
  {
    keywords: ["backend", "back-end", "back end", "api developer", "server-side"],
    tools: {
      junior: ["Python", "Node.js", "REST APIs", "PostgreSQL", "Git"],
      mid:    ["Python", "Node.js", "FastAPI", "PostgreSQL", "Redis", "Docker"],
      senior: ["Python", "Node.js", "Kubernetes", "PostgreSQL", "Redis", "Kafka", "AWS", "Docker"],
      lead:   ["Python", "Node.js", "Kafka", "PostgreSQL", "Redis", "AWS", "Kubernetes", "gRPC"],
    },
  },
  {
    keywords: ["fullstack", "full-stack", "full stack"],
    tools: {
      junior: ["React", "Node.js", "HTML", "CSS", "JavaScript", "PostgreSQL"],
      mid:    ["React", "Node.js", "TypeScript", "PostgreSQL", "Docker", "REST APIs"],
      senior: ["React", "Node.js", "TypeScript", "AWS", "Docker", "Kubernetes", "GraphQL"],
      lead:   ["React", "Node.js", "TypeScript", "AWS", "Docker", "Kubernetes", "GraphQL", "Terraform"],
    },
  },
  {
    keywords: ["devops", "site reliability", "sre", "platform engineer", "infrastructure"],
    tools: {
      junior: ["Docker", "Linux", "Git", "CI/CD", "AWS"],
      mid:    ["Docker", "Kubernetes", "Terraform", "AWS", "Jenkins", "CI/CD"],
      senior: ["Kubernetes", "Terraform", "AWS", "GCP", "Ansible", "Prometheus", "Grafana", "ArgoCD"],
      lead:   ["Kubernetes", "Terraform", "AWS", "GCP", "ArgoCD", "Prometheus", "Grafana", "Istio"],
    },
  },
  {
    keywords: ["data scientist", "data science", "ml engineer", "machine learning", "ai engineer"],
    tools: {
      junior: ["Python", "Pandas", "NumPy", "SQL", "Matplotlib", "Jupyter"],
      mid:    ["Python", "Scikit-learn", "TensorFlow", "SQL", "Pandas", "MLflow"],
      senior: ["Python", "PyTorch", "TensorFlow", "Spark", "MLflow", "SQL", "Airflow"],
      lead:   ["PyTorch", "TensorFlow", "Spark", "MLflow", "Airflow", "Kubeflow", "AWS SageMaker"],
    },
  },
  {
    keywords: ["data engineer", "data engineering", "etl", "pipeline engineer"],
    tools: {
      junior: ["Python", "SQL", "Pandas", "Git", "ETL"],
      mid:    ["Python", "Spark", "Airflow", "SQL", "dbt", "AWS"],
      senior: ["Spark", "Airflow", "Kafka", "dbt", "Snowflake", "AWS", "Kubernetes"],
      lead:   ["Spark", "Kafka", "Airflow", "dbt", "Snowflake", "AWS", "Databricks", "Terraform"],
    },
  },
  {
    keywords: ["data analyst", "business analyst", "analytics engineer"],
    tools: {
      junior: ["SQL", "Excel", "Tableau", "Google Analytics"],
      mid:    ["SQL", "Python", "Tableau", "Power BI", "dbt"],
      senior: ["SQL", "Python", "Tableau", "Power BI", "dbt", "Airflow", "Snowflake"],
      lead:   ["SQL", "Python", "Tableau", "Power BI", "Snowflake", "dbt", "Airflow"],
    },
  },
  {
    keywords: ["product manager", "product management", " pm "],
    tools: {
      junior: ["JIRA", "Confluence", "Figma", "Google Analytics"],
      mid:    ["JIRA", "Confluence", "Figma", "SQL", "Mixpanel", "Amplitude"],
      senior: ["JIRA", "Confluence", "Figma", "SQL", "Mixpanel", "Tableau", "Amplitude"],
      lead:   ["JIRA", "Confluence", "Figma", "SQL", "Mixpanel", "Tableau", "OKRs", "Amplitude"],
    },
  },
  {
    keywords: ["ux", "ui/ux", "product designer", "interaction designer", "graphic designer", "web designer"],
    tools: {
      junior: ["Figma", "Adobe XD", "Sketch", "InVision"],
      mid:    ["Figma", "Adobe Creative Suite", "Prototyping", "User Research", "Zeplin"],
      senior: ["Figma", "Adobe Creative Suite", "Design Systems", "User Research", "Prototyping"],
      lead:   ["Figma", "Adobe Creative Suite", "Design Systems", "User Research", "Design Tokens"],
    },
  },
  {
    keywords: ["ios developer", "ios engineer", "swift developer"],
    tools: {
      junior: ["Swift", "Xcode", "UIKit", "Git"],
      mid:    ["Swift", "SwiftUI", "UIKit", "Core Data", "REST APIs"],
      senior: ["Swift", "SwiftUI", "Combine", "Core Data", "XCTest", "CI/CD", "Instruments"],
      lead:   ["Swift", "SwiftUI", "Combine", "Core Data", "Instruments", "Firebase", "CI/CD"],
    },
  },
  {
    keywords: ["android developer", "android engineer", "kotlin developer"],
    tools: {
      junior: ["Kotlin", "Android Studio", "Java", "Git"],
      mid:    ["Kotlin", "Jetpack Compose", "Android Studio", "Room DB", "REST APIs"],
      senior: ["Kotlin", "Jetpack Compose", "Coroutines", "Dagger/Hilt", "Firebase", "CI/CD"],
      lead:   ["Kotlin", "Jetpack Compose", "Coroutines", "Dagger/Hilt", "Firebase", "CI/CD", "Espresso"],
    },
  },
  {
    keywords: ["mobile developer", "mobile engineer", "react native", "flutter developer"],
    tools: {
      junior: ["React Native", "Flutter", "JavaScript", "Git", "Firebase"],
      mid:    ["React Native", "Flutter", "Kotlin", "Swift", "Firebase", "CI/CD"],
      senior: ["React Native", "Flutter", "Kotlin", "Swift", "Firebase", "Detox", "CI/CD"],
      lead:   ["React Native", "Flutter", "Kotlin", "Swift", "Firebase", "Detox", "App Store Connect"],
    },
  },
  {
    keywords: ["security", "cybersecurity", "penetration", "ethical hacking", "infosec"],
    tools: {
      junior: ["Burp Suite", "Wireshark", "Linux", "Nmap", "OWASP"],
      mid:    ["Burp Suite", "Metasploit", "Wireshark", "Nmap", "Nessus", "OWASP"],
      senior: ["Burp Suite", "Metasploit", "Splunk", "SIEM", "Nessus", "AWS Security"],
      lead:   ["Burp Suite", "Metasploit", "Splunk", "SIEM", "SOC", "AWS Security", "Threat Modeling"],
    },
  },
  {
    keywords: ["cloud architect", "cloud engineer", "aws engineer", "azure engineer", "gcp engineer"],
    tools: {
      junior: ["AWS", "Azure", "GCP", "Docker", "Linux"],
      mid:    ["AWS", "Terraform", "Kubernetes", "Docker", "CI/CD"],
      senior: ["AWS", "GCP", "Terraform", "Kubernetes", "ArgoCD", "Prometheus", "Istio"],
      lead:   ["AWS", "GCP", "Azure", "Terraform", "Kubernetes", "ArgoCD", "Prometheus", "Istio"],
    },
  },
  {
    keywords: ["software engineer", "software developer", "sde", "swe", "software development"],
    tools: {
      junior: ["Python", "Java", "Git", "SQL", "REST APIs"],
      mid:    ["Python", "Java", "Docker", "PostgreSQL", "REST APIs", "CI/CD"],
      senior: ["Python", "Java", "Docker", "Kubernetes", "PostgreSQL", "Kafka", "AWS"],
      lead:   ["Python", "Java", "Docker", "Kubernetes", "Kafka", "AWS", "gRPC", "Terraform"],
    },
  },
];

function getToolSuggestions(roleName, seniority) {
  const name = ` ${(roleName || "").toLowerCase()} `;
  const tier = seniority.startsWith("Junior") ? "junior"
    : seniority.startsWith("Mid") ? "mid"
    : seniority.startsWith("Senior") ? "senior"
    : "lead";
  for (const entry of ROLE_TOOLS) {
    if (entry.keywords.some((kw) => name.includes(kw))) {
      return entry.tools[tier] || [];
    }
  }
  return [];
}

const ROUNDS = ["hr", "technical", "behavioral"];
const ROUND_LABELS = { hr: "HR", technical: "Technical", behavioral: "Behavioral" };
const ROUND_COLORS = {
  hr: "bg-[#0025D8] text-white",
  technical: "bg-[#0F172A] text-white",
  behavioral: "bg-[#7C3AED] text-white",
};
const MAX_DOCS = 5;
const MAX_SIZE_MB = 10;
const DIFFICULTY_TIER_MIN = 5;  // per-tier floor — below this a level may run thin (mirror of backend)
const DIFFICULTY_TIERS = ["Easy", "Medium", "Hard"];
const TIER_BAR = { Easy: "bg-[#10B981]", Medium: "bg-[#F59E0B]", Hard: "bg-[#E11D48]" };
const TIER_TEXT = { Easy: "text-[#10B981]", Medium: "text-[#B45309]", Hard: "text-[#E11D48]" };

function RoundTab({ round, active, onClick, qCount }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-3 font-mono text-xs uppercase tracking-wider font-semibold border-b-2 transition-colors ${
        active ? "border-[#0025D8] text-[#0025D8]" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {ROUND_LABELS[round]}
      <span className="ml-2 font-normal">({qCount})</span>
    </button>
  );
}

function FileStatusRow({ name, status, error }) {
  return (
    <div className="flex items-center gap-2 text-xs py-1">
      <span className="flex-shrink-0">
        {status === "uploading" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0025D8]" />}
        {status === "done"      && <Check   className="w-3.5 h-3.5 text-[#10B981]" />}
        {status === "failed"    && <X       className="w-3.5 h-3.5 text-[#E11D48]" />}
        {status === "pending"   && <span className="w-3.5 h-3.5 inline-block rounded-full border border-muted-foreground/40" />}
      </span>
      <span className={`truncate flex-1 ${status === "failed" ? "text-[#E11D48]" : "text-foreground"}`}>{name}</span>
      {error && <span className="text-[#E11D48] flex-shrink-0 text-[10px]">{error}</span>}
    </div>
  );
}

export default function AdminRoleDetail() {
  const { roleId } = useParams();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRound, setActiveRound] = useState("hr");
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const fileRef = useRef(null);
  const qualityPollRef = useRef(null);

  // AI doc generator state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSeniority, setAiSeniority] = useState("Mid-level (3–5 years)");
  const [aiTools, setAiTools] = useState("");
  const [aiToolSuggestions, setAiToolSuggestions] = useState(null); // null = not fetched; [] = fetched
  const [aiToolSuggestionsLoading, setAiToolSuggestionsLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDrafts, setAiDrafts] = useState(null); // { hr, technical, behavioral }
  const [aiSaving, setAiSaving] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(new Set(["hr", "technical", "behavioral"]));
  const [aiProgress, setAiProgress] = useState(0);
  const [aiMessageIdx, setAiMessageIdx] = useState(0);

  // Bulk generate state
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkStatus, setBulkStatus] = useState(null); // null = not run yet; { hr, technical, behavioral } during/after run

  // Coverage gap state
  const [gaps, setGaps] = useState({});
  const [dismissing, setDismissing] = useState(false);
  const [bankAlerts, setBankAlerts] = useState({});
  const [dismissingAlert, setDismissingAlert] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);
  const [suggestingFor, setSuggestingFor] = useState(null); // category string being suggested
  const [suggestions, setSuggestions] = useState([]);       // current suggestion cards
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [expandedAnswers, setExpandedAnswers] = useState(new Set());
  const [addingQ, setAddingQ] = useState(null); // q index being added
  const [expandedKeyPoints, setExpandedKeyPoints] = useState(new Set());

  const toggleKeyPoints = (q_id) => setExpandedKeyPoints((prev) => {
    const n = new Set(prev);
    n.has(q_id) ? n.delete(q_id) : n.add(q_id);
    return n;
  });

  const toggleTool = (tool) => {
    const current = aiTools.split(",").map((t) => t.trim()).filter(Boolean);
    const idx = current.findIndex((t) => t.toLowerCase() === tool.toLowerCase());
    if (idx >= 0) current.splice(idx, 1);
    else current.push(tool);
    setAiTools(current.join(", "));
  };

  const fetchAiToolSuggestions = async (seniority) => {
    setAiToolSuggestionsLoading(true);
    setAiToolSuggestions(null);
    try {
      const { data } = await api.post(`/admin/roles/${roleId}/suggest-tools`, { seniority });
      setAiToolSuggestions(data.tools || []);
    } catch {
      setAiToolSuggestions([]);
    } finally {
      setAiToolSuggestionsLoading(false);
    }
  };

  // When the panel opens: if no hardcoded match, fetch AI suggestions
  useEffect(() => {
    if (!aiOpen || !role) return;
    const hardcoded = getToolSuggestions(role.name, aiSeniority);
    if (hardcoded.length === 0) {
      fetchAiToolSuggestions(aiSeniority);
    } else {
      setAiToolSuggestions(null);
      setAiToolSuggestionsLoading(false);
    }
  }, [aiOpen, aiSeniority]); // eslint-disable-line react-hooks/exhaustive-deps

  const AI_MESSAGES = [
    "📝 Drafting HR interview questions and model answers…",
    "✨ Crafting model answers for the Technical round…",
    "💼 Building Behavioral STAR scenarios…",
    "🎯 Defining key talking points for each answer…",
    "🧠 Tailoring content to your seniority level…",
    "🔍 Reviewing question quality and coverage…",
    "📚 Structuring the coaching reference guide…",
    "⚡ Almost there — fine-tuning your interview guide…",
  ];

  useEffect(() => {
    if (!aiGenerating) return;
    setAiProgress(4);
    setAiMessageIdx(0);
    const startTime = Date.now();
    const DURATION = 58000; // ~58s estimated total for 3 parallel calls

    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      // Ease-out curve: fast start, slows as it approaches 88%
      const raw = Math.min(elapsed / DURATION, 1);
      const eased = 1 - Math.pow(1 - raw, 2);
      setAiProgress((prev) => Math.max(prev, Math.floor(eased * 88)));
    }, 250);

    const messageTimer = setInterval(() => {
      setAiMessageIdx((prev) => (prev + 1) % AI_MESSAGES.length);
    }, 4000);

    return () => {
      clearInterval(progressTimer);
      clearInterval(messageTimer);
    };
  }, [aiGenerating]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateAllBanks = async () => {
    const eligible = ROUNDS.filter((rnd) => (role.documents?.[rnd] || []).length > 0);
    if (eligible.length === 0) {
      toast.info("Upload documents to at least one round first");
      return;
    }

    // Initialise per-round status
    const init = {};
    ROUNDS.forEach((rnd) => {
      const hasDocs = (role.documents?.[rnd] || []).length > 0;
      if (!hasDocs) init[rnd] = { state: "no-docs", count: null };
      else init[rnd] = { state: "pending", count: null };
    });
    setBulkStatus(init);
    setBulkGenerating(true);

    let totalQ = 0;
    let roundsOk = 0;
    for (const rnd of eligible) {
      setBulkStatus((prev) => ({ ...prev, [rnd]: { state: "generating", count: null } }));
      try {
        const { data } = await api.post(`/admin/roles/${roleId}/questions/${rnd}/regenerate`);
        setBulkStatus((prev) => ({ ...prev, [rnd]: { state: "done", count: data.total } }));
        totalQ += data.total ?? 0;
        roundsOk++;
      } catch (e) {
        setBulkStatus((prev) => ({ ...prev, [rnd]: { state: "error", count: null } }));
        toast.error(`${ROUND_LABELS[rnd]} failed: ${e?.response?.data?.detail || "Generation failed"}`);
      }
    }

    if (roundsOk > 0) {
      toast.success(`Generated question banks for ${roundsOk} round${roundsOk !== 1 ? "s" : ""} — ${totalQ} total questions`);
    }
    await fetchRole();
    setBulkGenerating(false);
  };

  const generateReferenceDocs = async () => {
    setAiGenerating(true);
    setAiDrafts(null);
    try {
      const { data } = await api.post(`/admin/roles/${roleId}/generate-reference-docs`, {
        seniority: aiSeniority,
        tools: aiTools.trim() || null,
      });
      setAiProgress(100);
      setAiDrafts({ hr: data.hr, technical: data.technical, behavioral: data.behavioral });
      setAiExpanded(new Set(["hr", "technical", "behavioral"]));
      toast.success("Reference documents generated — review and save below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — please try again");
      setAiProgress(0);
    } finally {
      setAiGenerating(false);
      setTimeout(() => setAiProgress(0), 1200);
    }
  };

  const saveAiDocs = async () => {
    if (!aiDrafts) return;
    setAiSaving(true);
    const rounds = ["hr", "technical", "behavioral"];
    const seniority = aiSeniority.split(" (")[0].toLowerCase().replace(/\s+/g, "-");
    let saved = 0;
    let failed = 0;
    for (const rnd of rounds) {
      const text = aiDrafts[rnd];
      if (!text?.trim()) continue;
      try {
        await api.post(`/admin/roles/${roleId}/documents/${rnd}/text`, {
          text,
          filename: `ai-${rnd}-${seniority}.txt`,
        });
        saved++;
      } catch (e) {
        toast.error(`Failed to save ${rnd.toUpperCase()} document: ${e?.response?.data?.detail || "error"}`);
        failed++;
      }
    }
    setAiSaving(false);
    if (failed === 0) {
      toast.success(`${saved} reference documents saved — click Generate Question Bank on each round`);
      setAiOpen(false);
      setAiDrafts(null);
      setAiTools("");
      await fetchRole();
    }
  };

  const fetchRole = async () => {
    setLoading(true);
    try {
      const [roleRes, gapsRes, alertsRes] = await Promise.all([
        api.get(`/admin/roles/${roleId}`),
        api.get(`/admin/roles/${roleId}/coverage-gaps`).catch(() => ({ data: {} })),
        api.get(`/admin/roles/${roleId}/bank-alerts`).catch(() => ({ data: {} })),
      ]);
      setRole(roleRes.data);
      setGaps(gapsRes.data);
      setBankAlerts(alertsRes.data);
    } catch {
      toast.error("Could not load role");
    } finally { setLoading(false); }
  };

  const fetchGaps = async () => {
    try {
      const { data } = await api.get(`/admin/roles/${roleId}/coverage-gaps`);
      setGaps(data);
    } catch {}
  };

  useEffect(() => { fetchRole(); }, [roleId]); // eslint-disable-line
  useEffect(() => () => stopQualityPolling(), []); // eslint-disable-line

  const dismissBankAlert = async () => {
    setDismissingAlert(true);
    try {
      await api.post(`/admin/roles/${roleId}/bank-alerts/${activeRound}/dismiss`);
      setBankAlerts((prev) => { const n = { ...prev }; delete n[activeRound]; return n; });
      toast.success("Bank alert dismissed — add more questions to prevent repeats");
    } catch (e) {
      toast.error("Could not dismiss alert");
    } finally { setDismissingAlert(false); }
  };

  const topUpBank = async () => {
    setToppingUp(true);
    try {
      const { data } = await api.post(`/admin/roles/${roleId}/questions/${activeRound}/topup`);
      setBankAlerts((prev) => { const n = { ...prev }; delete n[activeRound]; return n; });
      await fetchRole();
      toast.success(`Added ${data.added} new questions — bank now has ${data.total} total`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed — try again");
    } finally { setToppingUp(false); }
  };

  const [generatingDifficulty, setGeneratingDifficulty] = useState({}); // { Easy: bool, Medium: bool, Hard: bool }
  const generateDifficultyQuestions = async (difficulty) => {
    setGeneratingDifficulty(prev => ({ ...prev, [difficulty]: true }));
    try {
      const { data } = await api.post(`/admin/roles/${roleId}/questions/${activeRound}/generate-difficulty`, { difficulty });
      await fetchRole();
      toast.success(`Added ${data.added} ${difficulty} question${data.added !== 1 ? "s" : ""} · bank now has ${data.total} total`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not generate ${difficulty} questions — try again`);
    } finally { setGeneratingDifficulty(prev => ({ ...prev, [difficulty]: false })); }
  };

  const dismissGaps = async () => {
    setDismissing(true);
    try {
      await api.post(`/admin/roles/${roleId}/coverage-gaps/${activeRound}/dismiss`);
      setSuggestions([]);
      setSuggestingFor(null);
      await fetchGaps();
      toast.success("Coverage gap alert dismissed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss");
    } finally { setDismissing(false); }
  };

  const jaccardSimilarity = (a, b) => {
    const words = (str) => new Set(str.toLowerCase().split(/\W+/).filter(Boolean));
    const setA = words(a);
    const setB = words(b);
    const intersection = [...setA].filter((w) => setB.has(w)).length;
    return intersection / (setA.size + setB.size - intersection);
  };

  const loadSuggestions = async (category) => {
    setSuggestingFor(category);
    setSuggestions([]);
    setExpandedAnswers(new Set());
    setLoadingSuggestions(true);
    try {
      const { data } = await api.post(`/admin/roles/${roleId}/coverage-gaps/${activeRound}/suggest`, { category });
      const raw = data.suggestions || [];

      // Frontend safety net: filter out suggestions too similar to existing bank questions
      const existingForCategory = (role.question_bank?.[activeRound] || [])
        .filter((q) => q.category === category && q.text)
        .map((q) => q.text);

      const filtered = raw.filter((s) =>
        !existingForCategory.some((eq) => jaccardSimilarity(s.text, eq) > 0.5)
      );

      if (filtered.length < raw.length) {
        toast.info(`${raw.length - filtered.length} suggestion${raw.length - filtered.length !== 1 ? "s" : ""} removed — too similar to existing questions`);
      }
      setSuggestions(filtered);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate suggestions");
      setSuggestingFor(null);
    } finally { setLoadingSuggestions(false); }
  };

  const addSuggestionToBank = async (s, idx) => {
    setAddingQ(idx);
    try {
      await api.post(`/admin/roles/${roleId}/questions/${activeRound}/add`, {
        text: s.text,
        tools: s.tools,
        category: s.category,
        sample_answer: s.sample_answer,
        key_points: s.key_points || [],
        difficulty: s.difficulty || "Medium",
      });
      setSuggestions((prev) => prev.filter((_, i) => i !== idx));
      setExpandedAnswers((prev) => { const n = new Set(prev); n.delete(idx); return n; });
      await fetchRole();
      toast.success("Question added to bank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add question");
    } finally { setAddingQ(null); }
  };

  const rejectSuggestion = (idx) => {
    setSuggestions((prev) => prev.filter((_, i) => i !== idx));
    setExpandedAnswers((prev) => { const n = new Set(prev); n.delete(idx); return n; });
  };

  const toggleAnswer = (idx) => {
    setExpandedAnswers((prev) => {
      const n = new Set(prev);
      n.has(idx) ? n.delete(idx) : n.add(idx);
      return n;
    });
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";

    const docs = role?.documents?.[activeRound] || [];
    const slots = MAX_DOCS - docs.length;

    // Validate size client-side; mark excess files beyond slot limit as failed immediately
    let slotsUsed = 0;
    const statuses = files.map((f) => {
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        return { name: f.name, status: "failed", error: `> ${MAX_SIZE_MB} MB`, file: null };
      }
      if (slotsUsed >= slots) {
        return { name: f.name, status: "failed", error: `${MAX_DOCS}-doc limit reached`, file: null };
      }
      slotsUsed++;
      return { name: f.name, status: "pending", error: null, file: f };
    });

    setFileStatuses(statuses);
    setUploading(true);

    let succeeded = 0;
    let failed = statuses.filter((s) => s.status === "failed").length;

    for (let i = 0; i < statuses.length; i++) {
      if (statuses[i].status === "failed") continue;

      setFileStatuses((prev) => prev.map((s, idx) => idx === i ? { ...s, status: "uploading" } : s));

      try {
        const form = new FormData();
        form.append("file", statuses[i].file);
        await api.post(`/admin/roles/${roleId}/documents/${activeRound}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFileStatuses((prev) => prev.map((s, idx) => idx === i ? { ...s, status: "done" } : s));
        succeeded++;
      } catch (err) {
        const msg = err?.response?.data?.detail || "Upload failed";
        setFileStatuses((prev) => prev.map((s, idx) => idx === i ? { ...s, status: "failed", error: msg } : s));
        failed++;
      }
    }

    await fetchRole();
    setUploading(false);

    if (failed === 0) {
      toast.success(`${succeeded} file${succeeded !== 1 ? "s" : ""} uploaded — click "Generate Question Bank" to build the bank`);
    } else if (succeeded === 0) {
      toast.error(`All ${failed} file${failed !== 1 ? "s" : ""} failed to upload`);
    } else {
      toast.warning(`${succeeded} uploaded, ${failed} failed — see details above`);
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await api.delete(`/admin/roles/${roleId}/documents/${activeRound}/${docId}`);
      toast.success("Document removed — click Generate / Regenerate Bank to refresh questions");
      await fetchRole();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete document");
    }
  };

  const generateBank = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/roles/${roleId}/questions/${activeRound}/regenerate`);
      toast.success(`Question bank generated — ${data.total} questions ready for review`);
      await fetchRole();
      setFileStatuses(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const regenerateBank = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/admin/roles/${roleId}/questions/${activeRound}/regenerate`);
      toast.success(`Regenerated ${data.total} questions`);
      await fetchRole();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed");
    } finally { setRegenerating(false); }
  };

  const [qualityChecking, setQualityChecking] = useState(false);

  const stopQualityPolling = () => {
    if (qualityPollRef.current) {
      clearInterval(qualityPollRef.current);
      qualityPollRef.current = null;
    }
  };

  const runQualityCheck = async () => {
    if (qualityChecking) return;
    setQualityChecking(true);
    try {
      await api.post(`/admin/roles/${roleId}/questions/${activeRound}/quality-check`);
      // Endpoint returns immediately — poll role doc until quality_check_running flag clears
      const round = activeRound;
      qualityPollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/admin/roles/${roleId}`);
          if (data.quality_check_running?.[round] === false) {
            stopQualityPolling();
            setRole(data);
            setQualityChecking(false);
            const bank = data.question_bank?.[round] || [];
            const fixedCount = bank.filter(q => q.auto_fixed).length;
            const score = data.question_bank_quality?.[round]?.score;
            toast.success(
              fixedCount > 0
                ? `Quality score: ${score ?? "—"}/100 · ${fixedCount} question${fixedCount !== 1 ? "s" : ""} auto-fixed`
                : score != null ? `Quality score: ${score}/100` : "Quality check complete"
            );
          }
        } catch {}
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quality check failed");
      setQualityChecking(false);
    }
  };

  const dismissTopup = async () => {
    try {
      await api.post(`/admin/roles/${roleId}/topup-notifications/dismiss`, { round_key: activeRound });
      await fetchRole();
    } catch { }
  };

  const dismissSkillGapTopup = async () => {
    try {
      await api.post(`/admin/roles/${roleId}/skill-gap-notifications/dismiss`, { round_key: activeRound });
      await fetchRole();
    } catch { }
  };

  const toggleApprove = async (q) => {
    try {
      await api.patch(`/admin/roles/${roleId}/questions/${activeRound}/${q.q_id}/approve`, {
        is_approved: !q.is_approved,
      });
      setRole((r) => {
        const bank = r.question_bank[activeRound].map((x) =>
          x.q_id === q.q_id ? { ...x, is_approved: !q.is_approved } : x
        );
        return { ...r, question_bank: { ...r.question_bank, [activeRound]: bank } };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update question");
    }
  };

  const cycleDifficulty = async (q) => {
    const next = { Easy: "Medium", Medium: "Hard", Hard: "Easy" }[q.difficulty || "Medium"];
    try {
      await api.patch(`/admin/roles/${roleId}/questions/${activeRound}/${q.q_id}/difficulty`, null, { params: { difficulty: next } });
      setRole((r) => {
        const bank = r.question_bank[activeRound].map((x) =>
          x.q_id === q.q_id ? { ...x, difficulty: next } : x
        );
        return { ...r, question_bank: { ...r.question_bank, [activeRound]: bank } };
      });
    } catch (e) {
      toast.error("Could not update difficulty");
    }
  };

  const saveEdit = async (q) => {
    if (!editText.trim()) return;
    try {
      await api.patch(`/admin/roles/${roleId}/questions/${activeRound}/${q.q_id}`, { text: editText.trim() });
      setRole((r) => {
        const bank = r.question_bank[activeRound].map((x) =>
          x.q_id === q.q_id ? { ...x, text: editText.trim() } : x
        );
        return { ...r, question_bank: { ...r.question_bank, [activeRound]: bank } };
      });
      setEditingId(null);
      toast.success("Question updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>;
  if (!role) return <div className="p-12 font-mono text-sm text-muted-foreground">Role not found.</div>;

  const docs = role.documents?.[activeRound] || [];
  const bank = role.question_bank?.[activeRound] || [];
  const approvedBank = bank.filter((q) => q.is_approved);
  const approvedCount = approvedBank.length;

  // Live difficulty coverage for this round (updates as difficulty is toggled)
  const diffCounts = approvedBank.reduce((acc, q) => {
    const d = DIFFICULTY_TIERS.includes(q.difficulty) ? q.difficulty : "Medium";
    acc[d] += 1;
    return acc;
  }, { Easy: 0, Medium: 0, Hard: 0 });
  const thinTiers = DIFFICULTY_TIERS.filter((t) => approvedCount > 0 && diffCounts[t] < DIFFICULTY_TIER_MIN);
  const atDocLimit = docs.length >= MAX_DOCS;
  const bankVersion = role.question_bank_version?.[activeRound];
  const bankNeedsRegen = bank.length > 0 && (!bankVersion || bankVersion < CURRENT_BANK_PROMPT_VERSION);
  const quality = role.question_bank_quality?.[activeRound] || null;
  const flaggedMap = new Map((quality?.flagged || []).map((f) => [f.q_id, f.issue]));
  const topupInfo = role.auto_topup_pending?.[activeRound] || null;
  const topupVisible = topupInfo && (Date.now() - new Date(topupInfo.triggered_at).getTime() < 86400000);
  const skillGapFills = ((role.skill_gap_topup?.[activeRound]) || []).filter(
    f => (Date.now() - new Date(f.triggered_at).getTime()) < 86400000
  );
  const skillGapVisible = skillGapFills.length > 0;

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin/roles" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-[#0025D8] inline-flex items-center gap-1.5 transition-colors group">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> Roles
        </Link>
        <div className="flex items-start justify-between mt-2 flex-wrap gap-4">
          <div>
            <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A]">{role.name}</h1>
            {role.description && <p className="text-muted-foreground mt-1">{role.description}</p>}
          </div>
          <span className={`px-3 py-1 text-xs font-mono font-bold uppercase tracking-wider ${role.is_active ? "bg-[#10B981] text-white" : "bg-secondary text-muted-foreground"}`}>
            {role.is_active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* Bulk generate action bar — shown when any round has documents */}
      {ROUNDS.some((rnd) => (role.documents?.[rnd] || []).length > 0) && (() => {
        const eligible = ROUNDS.filter((rnd) => (role.documents?.[rnd] || []).length > 0);
        const hasAnyExistingBank = eligible.some((rnd) => (role.question_bank?.[rnd] || []).length > 0);

        const statusIcon = (rnd) => {
          const s = bulkStatus?.[rnd];
          if (!s) return null;
          if (s.state === "generating") return <Loader2 className="w-3 h-3 animate-spin text-[#0025D8]" />;
          if (s.state === "done")       return <Check   className="w-3 h-3 text-[#10B981]" />;
          if (s.state === "skipped")    return <span className="w-3 h-3 font-mono text-[10px] text-muted-foreground leading-none">—</span>;
          if (s.state === "error")      return <X       className="w-3 h-3 text-[#E11D48]" />;
          if (s.state === "pending")    return <span className="w-3 h-3 inline-block rounded-full border border-muted-foreground/40" />;
          return null;
        };

        const statusLabel = (rnd) => {
          const s = bulkStatus?.[rnd];
          const docCount = (role.documents?.[rnd] || []).length;
          const qCount = (role.question_bank?.[rnd] || []).filter((q) => q.is_approved).length;
          if (!s) {
            if (docCount === 0) return <span className="text-muted-foreground">no docs</span>;
            if (qCount > 0) return <span className="text-[#10B981] font-semibold">{qCount} questions</span>;
            return <span className="text-[#F59E0B] font-semibold">needs generation</span>;
          }
          if (s.state === "generating") return <span className="text-[#0025D8]">generating…</span>;
          if (s.state === "done")       return <span className="text-[#10B981] font-semibold">{s.count} questions</span>;
          if (s.state === "skipped")    return <span className="text-muted-foreground">{s.count} questions (skipped)</span>;
          if (s.state === "error")      return <span className="text-[#E11D48]">failed</span>;
          if (s.state === "pending")    return <span className="text-muted-foreground">waiting…</span>;
          if (s.state === "no-docs")    return <span className="text-muted-foreground">no docs</span>;
          return null;
        };

        return (
          <div className="rounded-xl border border-border mb-8">
            <div className="flex items-center justify-between gap-4 px-4 py-3 flex-wrap bg-secondary/20">
              <div className="flex items-center gap-6 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Question Banks</span>
                {ROUNDS.map((rnd) => (
                  <div key={rnd} className="flex items-center gap-1.5">
                    {statusIcon(rnd)}
                    <span className="font-mono text-xs font-semibold">{ROUND_LABELS[rnd]}</span>
                    <span className="font-mono text-xs">— {statusLabel(rnd)}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={generateAllBanks}
                disabled={bulkGenerating || eligible.length === 0}
                className="rounded-xl h-9 text-xs bg-[#0025D8] hover:bg-[#001CB3] text-white disabled:opacity-50 flex-shrink-0"
              >
                <Sparkles className={`w-3.5 h-3.5 mr-2 ${bulkGenerating ? "animate-pulse" : ""}`} />
                {bulkGenerating
                  ? "Generating…"
                  : hasAnyExistingBank
                  ? `Regenerate All Question Banks (${eligible.length})`
                  : `Generate All Question Banks (${eligible.length})`}
              </Button>
            </div>
          </div>
        );
      })()}

      {/* Cross-round coverage gap summary */}
      {Object.keys(gaps).some((rnd) => gaps[rnd]?.gaps?.length > 0) && (
        <div className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-4 py-3 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#F59E0B] mb-2">
                Coverage gaps detected — click any category to jump to that round and generate questions
              </p>
              <div className="space-y-2">
                {ROUNDS.filter((rnd) => gaps[rnd]?.gaps?.length > 0).map((rnd) => (
                  <div key={rnd} className="flex items-start gap-2 flex-wrap">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#F59E0B]/70 pt-1 w-20 flex-shrink-0">
                      {ROUND_LABELS[rnd]}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {gaps[rnd].gaps.map((cat) => (
                        <button
                          key={cat}
                          onClick={() => { setActiveRound(rnd); loadSuggestions(cat); }}
                          disabled={loadingSuggestions}
                          className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${
                            suggestingFor === cat && activeRound === rnd
                              ? "bg-[#F59E0B] text-white border-[#F59E0B]"
                              : "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/40 hover:bg-[#F59E0B]/20"
                          }`}
                        >
                          {loadingSuggestions && suggestingFor === cat && activeRound === rnd
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Sparkles className="w-3 h-3" />}
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Round tabs */}
      <div className="flex border-b border-border mb-8">
        {ROUNDS.map((rnd) => (
          <RoundTab
            key={rnd}
            round={rnd}
            active={activeRound === rnd}
            onClick={() => { setActiveRound(rnd); setFileStatuses(null); setSuggestions([]); setSuggestingFor(null); setExpandedKeyPoints(new Set()); }}
            qCount={(role.question_bank?.[rnd] || []).filter((q) => q.is_approved).length}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Reference documents */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-border">
            <div className={`px-4 py-3 ${ROUND_COLORS[activeRound]}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em]">{ROUND_LABELS[activeRound]} Round</div>
              <div className="font-semibold text-sm mt-0.5">Reference Documents</div>
            </div>
            <div className="p-4 space-y-3">
              {docs.length === 0 ? (
                <p className="text-xs text-muted-foreground">No documents uploaded yet. Upload reference docs then click Generate Question Bank.</p>
              ) : (
                docs.map((doc) => (
                  <div key={doc.doc_id} className="flex items-start justify-between gap-2 rounded-xl border border-border p-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold truncate">{doc.filename}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {doc.size ? `${Math.round(doc.size / 1024)} KB` : ""}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => deleteDoc(doc.doc_id)} className="text-muted-foreground hover:text-[#E11D48] flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}

              {/* Per-file upload progress */}
              {fileStatuses && (
                <div className="rounded-xl border border-border p-3 bg-secondary/30 space-y-0.5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Upload progress</div>
                  {fileStatuses.map((s, i) => (
                    <FileStatusRow key={i} name={s.name} status={s.status} error={s.error} />
                  ))}
                </div>
              )}

              <div className="font-mono text-[10px] text-muted-foreground">
                {docs.length} / {MAX_DOCS} documents · max {MAX_SIZE_MB} MB each
              </div>

              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.docx,.txt,.md"
                className="hidden"
                onChange={uploadFiles}
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading || atDocLimit}
                variant="outline"
                className="w-full rounded-xl h-10 text-xs"
              >
                <Upload className="w-3.5 h-3.5 mr-2" />
                {uploading
                  ? "Uploading…"
                  : atDocLimit
                  ? `Limit reached (${MAX_DOCS}/${MAX_DOCS})`
                  : `Upload documents (${docs.length}/${MAX_DOCS})`}
              </Button>

              {/* AI Document Generator */}
              <Button
                onClick={() => { setAiOpen((v) => !v); setAiDrafts(null); }}
                disabled={uploading || atDocLimit}
                variant="outline"
                className={`w-full rounded-xl h-10 text-xs border-dashed ${aiOpen ? "border-[#0025D8] text-[#0025D8] bg-[#0025D8]/5" : "hover:border-[#0025D8] hover:text-[#0025D8]"}`}
              >
                <Wand2 className="w-3.5 h-3.5 mr-2" />
                {aiOpen ? "Close AI Generator" : "Generate with AI"}
              </Button>

              {aiOpen && (
                <div className="rounded-lg border border-[#0025D8]/30 bg-[#0025D8]/[0.02] p-4 space-y-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0025D8] mb-1">AI Reference Doc Generator</div>

                  {/* Role name — read only */}
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Role</label>
                    <div className="rounded-xl border border-border px-3 py-2 text-xs font-semibold bg-secondary/40 text-muted-foreground">{role.name}</div>
                  </div>

                  {/* Seniority */}
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">Seniority</label>
                    <select
                      value={aiSeniority}
                      onChange={(e) => setAiSeniority(e.target.value)}
                      disabled={aiGenerating}
                      className="w-full rounded-xl border border-border rounded-xl px-3 py-2 text-xs bg-background focus:outline-none focus:ring-1 focus:ring-[#0025D8]"
                    >
                      <option>Junior (0–2 years)</option>
                      <option>Mid-level (3–5 years)</option>
                      <option>Senior (6–9 years)</option>
                      <option>Lead / Principal (10+ years)</option>
                    </select>
                  </div>

                  {/* Key tools */}
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block mb-1">
                      Key tools / technologies <span className="normal-case">(optional)</span>
                    </label>

                    {/* Tool suggestion chips — hardcoded first, AI fallback */}
                    {(() => {
                      const hardcoded = getToolSuggestions(role.name, aiSeniority);
                      const chips = hardcoded.length > 0 ? hardcoded : (aiToolSuggestions || []);
                      const isAiFallback = hardcoded.length === 0;

                      if (isAiFallback && aiToolSuggestionsLoading) {
                        return (
                          <div className="flex items-center gap-1.5 mb-2 text-[10px] font-mono text-muted-foreground">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Fetching suggestions…
                          </div>
                        );
                      }
                      if (chips.length === 0) return null;

                      const selectedSet = new Set(
                        aiTools.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean)
                      );
                      return (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {isAiFallback && (
                            <span className="self-center font-mono text-[9px] uppercase tracking-wider text-[#0025D8]/60 mr-0.5">
                              AI
                            </span>
                          )}
                          {chips.map((tool) => {
                            const on = selectedSet.has(tool.toLowerCase());
                            return (
                              <button
                                key={tool}
                                type="button"
                                disabled={aiGenerating}
                                onClick={() => toggleTool(tool)}
                                className={`px-2 py-0.5 text-[10px] font-mono border transition-colors rounded-sm
                                  ${on
                                    ? "bg-[#0025D8] border-[#0025D8] text-white"
                                    : "bg-background border-border text-muted-foreground hover:border-[#0025D8] hover:text-[#0025D8]"
                                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                              >
                                {on && <span className="mr-1">✓</span>}{tool}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}

                    <Input
                      value={aiTools}
                      onChange={(e) => setAiTools(e.target.value)}
                      disabled={aiGenerating}
                      placeholder="e.g. Selenium, Playwright, TestNG"
                      className="rounded-xl text-xs h-9"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Click chips to toggle · or type manually · comma-separated</p>
                  </div>

                  {aiGenerating ? (
                    <div className="space-y-2 py-1">
                      {/* Progress bar */}
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#0025D8]">Generating</span>
                        <span className="font-mono text-[10px] text-[#0025D8] tabular-nums">{aiProgress}%</span>
                      </div>
                      <div className="w-full h-2 bg-[#0025D8]/10 rounded-lg border border-[#0025D8]/20 overflow-hidden">
                        <div
                          className="h-full bg-[#0025D8] transition-all duration-300 ease-out"
                          style={{ width: `${aiProgress}%` }}
                        />
                      </div>
                      {/* Rotating message */}
                      <p className="font-mono text-[11px] text-muted-foreground pt-0.5 min-h-[1.25rem]">
                        {AI_MESSAGES[aiMessageIdx]}
                      </p>
                    </div>
                  ) : (
                    <Button
                      onClick={generateReferenceDocs}
                      className="w-full rounded-xl h-9 text-xs bg-[#0025D8] hover:bg-[#001CB3] text-white"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-2" />
                      Generate All 3 Rounds
                    </Button>
                  )}

                  {/* Generated drafts — editable preview */}
                  {aiDrafts && (
                    <div className="space-y-3 pt-1">
                      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#10B981]">Generated — review and edit before saving</div>

                      {["hr", "technical", "behavioral"].map((rnd) => (
                        <div key={rnd} className="rounded-xl border border-border">
                          <button
                            type="button"
                            onClick={() => setAiExpanded((prev) => {
                              const n = new Set(prev);
                              n.has(rnd) ? n.delete(rnd) : n.add(rnd);
                              return n;
                            })}
                            className={`w-full flex items-center justify-between px-3 py-2 font-mono text-[10px] uppercase tracking-wider font-semibold text-left ${ROUND_COLORS[rnd]}`}
                          >
                            <span>{ROUND_LABELS[rnd]} Reference</span>
                            {aiExpanded.has(rnd) ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                          {aiExpanded.has(rnd) && (
                            <Textarea
                              value={aiDrafts[rnd]}
                              onChange={(e) => setAiDrafts((prev) => ({ ...prev, [rnd]: e.target.value }))}
                              rows={10}
                              className="rounded-xl border-0 border-t border-border text-xs resize-y font-mono leading-relaxed focus-visible:ring-0"
                            />
                          )}
                        </div>
                      ))}

                      <div className="flex gap-2 pt-1">
                        <Button
                          onClick={saveAiDocs}
                          disabled={aiSaving}
                          className="flex-1 rounded-xl h-9 text-xs bg-[#10B981] hover:bg-[#059669] text-white"
                        >
                          {aiSaving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-2" />}
                          {aiSaving ? "Saving…" : "Save All Documents"}
                        </Button>
                        <Button
                          onClick={generateReferenceDocs}
                          disabled={aiGenerating || aiSaving}
                          variant="outline"
                          className="rounded-xl h-9 text-xs px-3"
                          title="Regenerate"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${aiGenerating ? "animate-spin" : ""}`} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Generate Question Bank */}
              <Button
                onClick={generateBank}
                disabled={generating || docs.length === 0 || uploading}
                className="w-full rounded-xl h-10 text-xs bg-[#0025D8] hover:bg-[#001CB3] text-white"
              >
                <Sparkles className={`w-3.5 h-3.5 mr-2 ${generating ? "animate-pulse" : ""}`} />
                {generating ? "Generating…" : "Generate Question Bank"}
              </Button>

              <p className="text-[10px] text-muted-foreground">PDF, DOCX, TXT or MD · select up to {MAX_DOCS} files at once.</p>
            </div>
          </div>
        </div>

        {/* Right: Question bank */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Question Bank</div>
                <div className="text-sm font-semibold mt-0.5">
                  {approvedCount} approved
                  <span className="text-muted-foreground font-normal"> / {bank.length} total</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl h-8 text-xs"
                onClick={regenerateBank}
                disabled={regenerating || docs.length === 0}
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${regenerating ? "animate-spin" : ""}`} />
                {regenerating ? "Regenerating…" : "Regenerate Bank"}
              </Button>
            </div>

            {/* ── Difficulty coverage meter ── one bank serves every experience level ── */}
            {approvedCount > 0 && (
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Difficulty coverage
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    juniors draw Easy · seniors draw Hard
                  </span>
                </div>
                <div className="flex h-2 w-full overflow-hidden rounded-full bg-secondary mb-2">
                  {DIFFICULTY_TIERS.map((t) => {
                    const pct = approvedCount ? (diffCounts[t] / approvedCount) * 100 : 0;
                    return pct > 0 ? <div key={t} className={TIER_BAR[t]} style={{ width: `${pct}%` }} title={`${t}: ${diffCounts[t]}`} /> : null;
                  })}
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {DIFFICULTY_TIERS.map((t) => {
                    const isGenerating = generatingDifficulty[t];
                    const canGenerate = docs.length > 0;
                    return (
                      <span key={t} className="inline-flex items-center gap-1.5 text-xs">
                        <span className={`w-2.5 h-2.5 rounded-sm ${TIER_BAR[t]}`} />
                        <span className={`font-semibold ${TIER_TEXT[t]}`}>{t}</span>
                        <span className="font-mono text-muted-foreground">{diffCounts[t]}</span>
                        {thinTiers.includes(t) && (
                          <span className="font-mono text-[9px] uppercase tracking-wider px-1 py-0.5 bg-[#F59E0B]/10 text-[#B45309] rounded border border-[#F59E0B]/30">thin</span>
                        )}
                        {canGenerate && (
                          <button
                            onClick={() => generateDifficultyQuestions(t)}
                            disabled={isGenerating || Object.values(generatingDifficulty).some(Boolean)}
                            title={`Generate 10 ${t} questions`}
                            className={`inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                              t === "Easy"   ? "border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981] hover:text-white" :
                              t === "Hard"   ? "border-[#E11D48]/40 text-[#E11D48] hover:bg-[#E11D48] hover:text-white" :
                                               "border-[#F59E0B]/40 text-[#B45309] hover:bg-[#F59E0B] hover:text-white"
                            }`}
                          >
                            {isGenerating
                              ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> …</>
                              : <>+ {t}</>}
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
                {thinTiers.length > 0 && (
                  <p className="mt-2 text-[11px] text-[#B45309] flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                    <span>
                      {thinTiers.join(" & ")} {thinTiers.length > 1 ? "tiers are" : "tier is"} below {DIFFICULTY_TIER_MIN} questions —
                      click the <strong>+ {thinTiers[0]}</strong> button above to add 10 more, or retag with the difficulty toggle on each question.
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* ── Auto top-up notification ── */}
            {topupVisible && (
              <div className="flex items-start gap-3 px-4 py-3 bg-[#D97706]/10 border-b border-[#D97706]/30">
                <span className="text-[#D97706] text-base flex-shrink-0 mt-0.5">↑</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-[#D97706]">Bank auto-topped up</p>
                  <p className="text-[11px] text-[#D97706]/80 mt-0.5">
                    {topupInfo.questions_added} questions were automatically added on{" "}
                    {new Date(topupInfo.triggered_at).toLocaleDateString()} because the unseen pool
                    dropped below {10}. New questions are auto-approved — review them below.
                  </p>
                </div>
                <button
                  onClick={dismissTopup}
                  className="flex-shrink-0 text-[#D97706]/60 hover:text-[#D97706] transition-colors text-xs font-mono uppercase tracking-wider"
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            )}

            {/* ── Skill-gap fill notification ── */}
            {skillGapVisible && (
              <div className="flex items-start gap-3 px-4 py-3 bg-[#6366F1]/10 border-b border-[#6366F1]/30">
                <span className="text-[#6366F1] text-base flex-shrink-0 mt-0.5">⚡</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-[#6366F1]">Skill-gap questions added</p>
                  <p className="text-[11px] text-[#6366F1]/80 mt-0.5">
                    {skillGapFills.map((f, i) => (
                      <span key={i}>
                        <strong>{f.questions_added}</strong> questions for <strong>{f.tool}</strong>
                        {i < skillGapFills.length - 1 ? ", " : ""}
                      </span>
                    ))}
                    {" "}were automatically added because a candidate had experience with {skillGapFills.length === 1 ? "this tool" : "these tools"} but the bank had no coverage. New questions are auto-approved — review them below.
                  </p>
                </div>
                <button
                  onClick={dismissSkillGapTopup}
                  className="flex-shrink-0 text-[#6366F1]/60 hover:text-[#6366F1] transition-colors text-xs font-mono uppercase tracking-wider"
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            )}

            {/* ── Bank quality (Phase 2 eval harness) ── */}
            {bank.length > 0 && (
              <div className="px-4 py-3 border-b border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Bank quality
                  </span>
                  <Button
                    variant="outline" size="sm"
                    className="rounded-xl h-7 text-[11px]"
                    onClick={runQualityCheck}
                    disabled={qualityChecking}
                  >
                    {qualityChecking
                      ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Running…</>
                      : <><Sparkles className="w-3 h-3 mr-1.5" /> {quality ? "Re-check & fix" : "Run quality check"}</>}
                  </Button>
                </div>
                {quality ? (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`text-2xl font-bold ${quality.score >= 80 ? "text-[#10B981]" : quality.score >= 60 ? "text-[#F59E0B]" : "text-[#E11D48]"}`}>
                        {quality.score != null ? quality.score : "—"}
                        <span className="text-xs font-normal text-muted-foreground">/100</span>
                      </div>
                      {quality.flagged?.length > 0 && (
                        <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#E11D48]/10 text-[#E11D48] rounded border border-[#E11D48]/30">
                          {quality.flagged.length} flagged
                        </span>
                      )}
                      {quality.evaluated_at && (
                        <span className="font-mono text-[10px] text-muted-foreground">
                          checked {new Date(quality.evaluated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {quality.dimensions && Object.keys(quality.dimensions).length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mb-2">
                        {Object.entries(quality.dimensions).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between gap-2">
                            <span className="text-[11px] text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                            <span className="font-mono text-[11px] font-semibold">{v != null ? v : "—"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {quality.summary && (
                      <p className="text-[11px] text-muted-foreground leading-snug">{quality.summary}</p>
                    )}
                  </>
                ) : (
                  <p className="text-[11px] text-muted-foreground">Not audited yet — run a quality check to score groundedness, specificity, difficulty accuracy, diversity and answer quality.</p>
                )}
              </div>
            )}

            {bankNeedsRegen && (
              <div className="flex items-start gap-3 px-4 py-3 bg-[#F59E0B]/10 border-b border-[#F59E0B]/30">
                <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-[#F59E0B]">Question bank may contain document references</p>
                  <p className="text-[11px] text-[#F59E0B]/80 mt-0.5">
                    This bank was generated with an older prompt that could leak section numbers or internal document structure to candidates. Regenerate to fix.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="rounded-xl h-7 text-[11px] bg-[#F59E0B] hover:bg-[#D97706] text-white flex-shrink-0"
                  onClick={regenerateBank}
                  disabled={regenerating || docs.length === 0}
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${regenerating ? "animate-spin" : ""}`} />
                  {regenerating ? "Regenerating…" : "Regenerate now"}
                </Button>
              </div>
            )}

            {/* ── Bank exhaustion alert ── */}
            {bankAlerts[activeRound] && (
              <div className="border-b border-[#E11D48]/30 bg-[#E11D48]/5">
                <div className="flex items-start gap-3 px-4 py-3">
                  <AlertTriangle className="w-4 h-4 text-[#E11D48] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#E11D48]">
                      Thin question bank — {bankAlerts[activeRound].affected_count} candidate{bankAlerts[activeRound].affected_count !== 1 ? "s" : ""} received repeated questions in this round
                    </p>
                    <p className="text-[11px] text-[#E11D48]/70 mt-0.5">
                      The bank ran out of fresh questions and served previously-seen ones. Add more questions below to ensure every candidate gets a unique set.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      className="rounded-xl h-7 text-[11px] bg-[#E11D48] hover:bg-[#BE123C] text-white"
                      onClick={topUpBank}
                      disabled={toppingUp || dismissingAlert}
                    >
                      {toppingUp ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Generating…</> : "+ Add 20 questions"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl h-7 text-[11px] border-[#E11D48]/40 text-[#E11D48] hover:bg-[#E11D48]/10"
                      onClick={dismissBankAlert}
                      disabled={dismissingAlert || toppingUp}
                    >
                      {dismissingAlert ? <Loader2 className="w-3 h-3 animate-spin" /> : "Dismiss"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Coverage gap alert ── */}
            {gaps[activeRound] && (
              <div className="border-b border-[#F59E0B]/30 bg-[#F59E0B]/5">
                <div className="flex items-start gap-3 px-4 py-3">
                  <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#F59E0B]">
                      Coverage gaps — {gaps[activeRound].count} session{gaps[activeRound].count !== 1 ? "s" : ""} hit missing categories
                    </p>
                    <p className="text-[11px] text-[#F59E0B]/70 mt-0.5 mb-2">
                      Candidates had skills in these categories but the bank had no matching questions. Click a category to get AI-suggested questions.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {gaps[activeRound].gaps.map((cat) => (
                        <button
                          key={cat}
                          onClick={() => loadSuggestions(cat)}
                          disabled={loadingSuggestions}
                          className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${
                            suggestingFor === cat
                              ? "bg-[#F59E0B] text-white border-[#F59E0B]"
                              : "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/40 hover:bg-[#F59E0B]/20"
                          }`}
                        >
                          {loadingSuggestions && suggestingFor === cat
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Sparkles className="w-3 h-3" />}
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-xl h-7 text-[11px] flex-shrink-0 border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10"
                    onClick={dismissGaps}
                    disabled={dismissing}
                  >
                    {dismissing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Dismiss"}
                  </Button>
                </div>

                {/* Suggestion cards */}
                {suggestions.length > 0 && (
                  <div className="px-4 pb-4 space-y-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F59E0B] mb-2">
                      AI Suggestions for "{suggestingFor}" — review and add to bank
                    </div>
                    {suggestions.map((s, idx) => (
                      <div key={idx} className="rounded-lg border border-[#F59E0B]/30 bg-white">
                        <div className="p-3">
                          <p className="text-sm leading-relaxed text-foreground">{s.text}</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] rounded-lg border border-[#F59E0B]/20">{s.category}</span>
                            <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${
                              s.difficulty === "Easy" ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30" :
                              s.difficulty === "Hard" ? "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" :
                                                        "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30"
                            }`}>{s.difficulty || "Medium"}</span>
                            {(s.tools || []).map((t) => (
                              <span key={t} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-xl border border-border">{t}</span>
                            ))}
                          </div>
                          {s.key_points?.length > 0 && (
                            <div className="mt-2">
                              <button
                                onClick={() => toggleKeyPoints(`suggestion-${idx}`)}
                                className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-[#0025D8] transition-colors group"
                              >
                                {expandedKeyPoints.has(`suggestion-${idx}`)
                                  ? <ChevronUp className="w-3 h-3 group-hover:text-[#0025D8]" />
                                  : <ChevronDown className="w-3 h-3 group-hover:text-[#0025D8]" />}
                                <span className="uppercase tracking-wider">Key Points</span>
                                <span className="ml-1 px-1 py-0 bg-secondary rounded-xl border border-border text-[9px] font-semibold rounded-sm">
                                  {s.key_points.length}
                                </span>
                              </button>
                              {expandedKeyPoints.has(`suggestion-${idx}`) && (
                                <ul className="mt-1.5 ml-0.5 space-y-1 border-l-2 border-[#0025D8]/20 pl-3">
                                  {s.key_points.map((pt, i) => (
                                    <li key={i} className="flex items-start gap-1.5">
                                      <span className="text-[#0025D8] font-bold text-[10px] flex-shrink-0 mt-0.5">·</span>
                                      <span className="text-[11px] text-muted-foreground leading-snug">{pt}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                          {s.sample_answer && (
                            <div className="mt-2">
                              <button
                                onClick={() => toggleAnswer(idx)}
                                className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-foreground"
                              >
                                {expandedAnswers.has(idx) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                {expandedAnswers.has(idx) ? "Hide" : "View"} model answer
                              </button>
                              {expandedAnswers.has(idx) && (
                                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed border-l-2 border-[#F59E0B]/40 pl-3 italic">{s.sample_answer}</p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 px-3 pb-3">
                          <Button
                            size="sm"
                            className="rounded-xl h-7 text-[11px] bg-[#10B981] hover:bg-[#059669] text-white"
                            onClick={() => addSuggestionToBank(s, idx)}
                            disabled={addingQ === idx}
                          >
                            {addingQ === idx ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                            Add to bank
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-xl h-7 text-[11px] text-muted-foreground hover:text-[#E11D48] hover:border-[#E11D48]"
                            onClick={() => rejectSuggestion(idx)}
                            disabled={addingQ === idx}
                          >
                            <Ban className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {bank.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                {docs.length === 0
                  ? "Upload reference documents, then click Generate Question Bank."
                  : "Click Generate Question Bank to create questions from your uploaded documents."}
              </div>
            ) : (
              <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {bank.map((q, idx) => (
                  <div
                    key={q.q_id}
                    className={`p-4 flex gap-3 items-start ${!q.is_approved ? "opacity-50 bg-secondary/20" : ""}`}
                  >
                    <span className="font-mono text-[10px] text-muted-foreground pt-1 w-7 flex-shrink-0">
                      {String(idx + 1).padStart(2, "0")}
                    </span>

                    <div className="flex-1 min-w-0">
                      {editingId === q.q_id ? (
                        <div className="flex gap-2">
                          <Input
                            autoFocus
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(q); if (e.key === "Escape") setEditingId(null); }}
                            className="rounded-xl py-2 text-sm flex-1"
                          />
                          <Button size="sm" variant="outline" className="rounded-xl h-9 px-2" onClick={() => saveEdit(q)}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-xl h-9 px-2" onClick={() => setEditingId(null)}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm leading-relaxed">{q.text}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {q.category && (
                          <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] rounded-lg border border-[#1D4ED8]/20">
                            {q.category}
                          </span>
                        )}
                        {q.source === "admin_approved" && (
                          <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] rounded-lg border border-[#10B981]/30">
                            Admin Added
                          </span>
                        )}
                        {q.source === "auto_topup" && (
                          <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#D97706]/10 text-[#D97706] rounded-lg border border-[#D97706]/30" title="Added automatically by bank top-up">
                            ↑ Auto-added
                          </span>
                        )}
                        {q.source === "skill_gap_fill" && (
                          <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#6366F1]/10 text-[#6366F1] rounded-lg border border-[#6366F1]/30" title={`Added to fill skill gap: ${q.gap_tool || "unknown tool"}`}>
                            ⚡ {q.gap_tool || "Skill-gap"}
                          </span>
                        )}
                        {q.source === "difficulty_topup" && (
                          <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg border ${
                            q.difficulty === "Easy"   ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30" :
                            q.difficulty === "Hard"   ? "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" :
                                                        "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30"
                          }`} title={`Added via + ${q.difficulty} button`}>
                            ↑ {q.difficulty}-added
                          </span>
                        )}
                        {q.auto_fixed && (
                          <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] rounded-lg border border-[#10B981]/30" title="Rewritten automatically by quality auto-fix">
                            ✓ Auto-fixed
                          </span>
                        )}
                        <button
                          onClick={() => cycleDifficulty(q)}
                          title="Click to change difficulty"
                          className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border cursor-pointer transition-opacity hover:opacity-70 ${
                            q.difficulty === "Easy"   ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30" :
                            q.difficulty === "Hard"   ? "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" :
                                                        "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30"
                          }`}
                        >
                          {q.difficulty || "Medium"}
                        </button>
                        {flaggedMap.has(q.q_id) && (
                          <span
                            title={flaggedMap.get(q.q_id) || "Flagged by quality check"}
                            className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#E11D48]/10 text-[#E11D48] border border-[#E11D48]/30 rounded-xl cursor-help"
                          >
                            ⚑ flagged
                          </span>
                        )}
                        {(q.tools || []).map((tool) => (
                          <span key={tool} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-xl border border-border">
                            {tool}
                          </span>
                        ))}
                      </div>
                      {/* Key points expand/collapse */}
                      {q.key_points && q.key_points.length > 0 && (
                        <div className="mt-2">
                          <button
                            onClick={() => toggleKeyPoints(q.q_id)}
                            className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-[#0025D8] transition-colors group"
                          >
                            {expandedKeyPoints.has(q.q_id)
                              ? <ChevronUp className="w-3 h-3 group-hover:text-[#0025D8]" />
                              : <ChevronDown className="w-3 h-3 group-hover:text-[#0025D8]" />}
                            <span className="uppercase tracking-wider">
                              Key Points
                            </span>
                            <span className="ml-1 px-1 py-0 bg-secondary rounded-xl border border-border text-[9px] font-semibold rounded-sm">
                              {q.key_points.length}
                            </span>
                          </button>
                          {expandedKeyPoints.has(q.q_id) && (
                            <ul className="mt-1.5 ml-0.5 space-y-1 border-l-2 border-[#0025D8]/20 pl-3">
                              {q.key_points.map((pt, i) => (
                                <li key={i} className="flex items-start gap-1.5">
                                  <span className="text-[#0025D8] font-bold text-[10px] flex-shrink-0 mt-0.5">·</span>
                                  <span className="text-[11px] text-muted-foreground leading-snug">{pt}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      {/* Model answer expand/collapse */}
                      {q.model_answer && (
                        <div className="mt-2">
                          <button
                            onClick={() => toggleKeyPoints(`ma-${q.q_id}`)}
                            className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-[#10B981] transition-colors group"
                          >
                            {expandedKeyPoints.has(`ma-${q.q_id}`)
                              ? <ChevronUp className="w-3 h-3 group-hover:text-[#10B981]" />
                              : <ChevronDown className="w-3 h-3 group-hover:text-[#10B981]" />}
                            <span className="uppercase tracking-wider">Model Answer</span>
                          </button>
                          {expandedKeyPoints.has(`ma-${q.q_id}`) && (
                            <p className="mt-1.5 ml-0.5 border-l-2 border-[#10B981]/30 pl-3 text-[11px] text-muted-foreground leading-relaxed whitespace-pre-line">
                              {q.model_answer}
                            </p>
                          )}
                        </div>
                      )}
                      {q.edited_at && (
                        <span className="font-mono text-[10px] text-muted-foreground">edited</span>
                      )}
                    </div>

                    {editingId !== q.q_id && (
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => { setEditingId(q.q_id); setEditText(q.text); }}
                          className="p-1.5 text-muted-foreground hover:text-foreground"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => toggleApprove(q)}
                          className={`p-1.5 ${q.is_approved ? "text-[#10B981] hover:text-red-500" : "text-muted-foreground hover:text-[#10B981]"}`}
                          title={q.is_approved ? "Hide question" : "Approve question"}
                        >
                          {q.is_approved ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
